import React, { useState, useEffect } from "react";
import moment from "moment";
import { Link } from "react-router-dom";

import Loading from "../components/common/loading";
import ListGroup from "../components/common/listGroup";
import ParameterChart from "../components/parameterChart";

import http from "../services/http";
import { toast } from "react-toastify";

const hourlyDateRanges = [
  { history: 1, type: "day", label: "1D" },
  { history: 2, type: "day", label: "2D" },
  { history: 3, type: "day", label: "3D" },
  { history: 1, type: "week", label: "1W" },
];
const dailyDateRanges = [
  { history: 1, type: "week", label: "1W" },
  { history: 2, type: "week", label: "2W" },
  { history: 1, type: "month", label: "1M" },
  { history: 3, type: "month", label: "3M" },
];
const longDateRanges = [
  { history: 1, type: "month", label: "1M" },
  { history: 3, type: "month", label: "3M" },
  { history: 6, type: "month", label: "6M" },
  { history: 1, type: "year", label: "1Y" },
];

const Parameter = ({ match }) => {
  const [data, setData] = useState([]);
  const [parameter, setParameter] = useState({});
  const [periods, setPeriods] = useState([]);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [site, setSite] = useState("");
  const [dateRange, setDateRange] = useState("");

  const sites = [
    { id: "BA", name: "Bardney" },
    { id: "BU", name: "Bury" },
    { id: "CA", name: "Cantley" },
    { id: "NE", name: "Newark" },
    { id: "WI", name: "Wissington" },
  ];

  useEffect(() => {
    if (match.params.site) {
      setSite({ id: match.params.site });
    }
  }, [match.params.site]);

  useEffect(() => {
    setPeriods([]);
    setRelated([]);
    const period = match.params.code.substr(-1, 1).toUpperCase();
    if (period === "W" || period === "T") {
      setDateRange(longDateRanges[0]);
    } else if (period === "H") {
      setDateRange(hourlyDateRanges[0]);
    } else {
      setDateRange(dailyDateRanges[0]);
    }
  }, [match.params.code]);

  useEffect(() => {
    async function getParameterInfo() {
      setParameter({});
      const code = match.params.code.toUpperCase();
      try {
        const { data } = await http.get(`/parameter/${site.id}/${code}`);
        const { data: periods } = await http.get(`/parameter-links/period/${code}`);
        const { data: linked } = await http.get(`/parameter-links/${code}`);
        setPeriods(periods);
        setRelated(linked);
        setParameter(mapToView(data));
      } catch (error) {
        if (error.response && error.response.status < 500) {
          toast.error(error.response.data);
        }
      }
    }
    async function getAllInfo() {
      setLoading(true);
      await getParameterInfo();
      setLoading(false);
    }
    if (site) {
      getAllInfo();
    }
  }, [match.params.code, site]);

  useEffect(() => {
    async function getParameterData() {
      setData([]);
      const code = parameter.CODE;
      const toDate = moment().format("YYYY-MM-DD");
      const fromDate = moment()
        .subtract(dateRange.history, dateRange.type)
        .format("YYYY-MM-DD");

      try {
        const { data } = await http.get(`/data/${site.id}/${code}/${fromDate}/${toDate}`);
        setData(data);
      } catch (error) {
        console.log(error);
      }
    }
    async function getInfo() {
      setLoadingData(true);
      await getParameterData();
      setLoadingData(false);
    }
    if (site && dateRange) {
      getInfo();
    }
  }, [parameter, site, dateRange]);

  function mapToView(parameter) {
    return {
      CODE: parameter.CODE,
      HEADING1: parameter.HEADING1 ? parameter.HEADING1 : "",
      HEADING2: parameter.HEADING2 ? parameter.HEADING2 : "",
      HEADING3: parameter.HEADING3 ? parameter.HEADING3 : "",
      PTYP_CODE: parameter.PTYP_CODE,
      TYPE: parameter.TYPE,
      SFNC_CODE: parameter.SFNC_CODE ? parameter.SFNC_CODE : "",
      STANDARD_FUNCTION: parameter.STANDARD_FUNCTION
        ? parameter.STANDARD_FUNCTION
        : "",
      PROCESS_TAG: parameter.PROCESS_TAG ? parameter.PROCESS_TAG : "",
      DISPLAY_PRECISION: parameter.DISPLAY_PRECISION,
      LOCAL_FLAG: parameter.LOCAL_FLAG,
      CGRP_CODE: parameter.CGRP_CODE ? parameter.CGRP_CODE : "",
      IN_USE_FLAG: parameter.IN_USE_FLAG,
      UPPER_ABS_LIMIT:
        parameter.UPPER_ABS_LIMIT !== null ? parameter.UPPER_ABS_LIMIT : "",
      UPPER_CHK_LIMIT:
        parameter.UPPER_CHK_LIMIT !== null ? parameter.UPPER_CHK_LIMIT : "",
      LOWER_CHK_LIMIT:
        parameter.LOWER_CHK_LIMIT !== null ? parameter.LOWER_CHK_LIMIT : "",
      LOWER_ABS_LIMIT:
        parameter.LOWER_ABS_LIMIT !== null ? parameter.LOWER_ABS_LIMIT : "",
      CONSTANTS_DEFAULT_VALUE:
        parameter.CONSTANTS_DEFAULT_VALUE !== null
          ? parameter.CONSTANTS_DEFAULT_VALUE
          : "",
    };
  }

  const getDateRange = () => {
    const period = match.params.code.substr(-1, 1).toUpperCase();
    if (period === "W" || period === "T") {
      return longDateRanges;
    } else if (period === "H") {
      return hourlyDateRanges;
    } else {
      return dailyDateRanges;
    }
  };

  let filtered = [...data];
  filtered = data.filter((d) => {
    return !d.PARM_CODE.endsWith("W") || moment(d.PARAM_DATE).day() === 1;
  });

  return (
    <div className="col mt-4">
      {loading && <Loading />}
      <div className="row">
        <div className="col">
          <div className="overflow-auto" style={{ maxHeight: "90vh" }}>
            {loadingData ? (
              <Loading />
            ) : (
              <>
                <table className="table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Operation</th>
                      <th>Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((record, i) => (
                      <tr
                        key={i}
                        className={getTableRowClass(parameter, record)}
                      >
                        <td>
                          {moment(record.REAL_DATETIME).format(
                            "Do MMM YY HH:mm"
                          )}
                        </td>
                        <td>{record.OPTN_CODE}</td>
                        <td>{record.PARAM_VALUE}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {filtered.length === 0 && (
                  <p className="text-center lead">No Recent Data</p>
                )}
              </>
            )}
          </div>
        </div>
        <div className="col">
          <div className="row justify-content-center">
            <ListGroup
              items={sites}
              selectedItem={site}
              onItemSelect={(item) => setSite(item)}
            />
          </div>
          <div className="row justify-content-center">
            <ListGroup
              items={getDateRange()}
              selectedItem={dateRange}
              onItemSelect={(item) => setDateRange(item)}
              textProperty={"label"}
              valueProperty={"label"}
            />
          </div>
          <div className="d-flex justify-content-center mb-3">
            <div className="card shadow-sm" style={{ maxWidth: "20vw" }}>
              <div className="card-body">
                <h4 className="card-title text-center">{match.params.code}</h4>
                <h5 className="card-subtitle text-success mb-2 text-center ">
                  {parameter.HEADING1} {parameter.HEADING2} {parameter.HEADING3}
                </h5>
                {parameter.CODE && (
                  <p className="text-center">
                    {parameter.TYPE}
                    {parameter.STANDARD_FUNCTION &&
                      " - " + parameter.STANDARD_FUNCTION}
                  </p>
                )}
                {parameter.CODE && (
                  <table className="table table-borderless table-sm text-center">
                    <thead>
                      <tr>
                        <td>Lower Absolute</td>
                        <td>Lower Check</td>
                        <td>Upper Check</td>
                        <td>Upper Absolute</td>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>{parameter.LOWER_ABS_LIMIT}</td>
                        <td>{parameter.LOWER_CHK_LIMIT}</td>
                        <td>{parameter.UPPER_CHK_LIMIT}</td>
                        <td>{parameter.UPPER_ABS_LIMIT}</td>
                      </tr>
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
          <div className="row mb-2">
            {periods.map((link) => (
              <Link to={`/parameter/${site.id}/${link.CODE}`} key={link.CODE}>
                <button className="btn btn-light btn-sm mr-3">
                  {link.DESCRIPTION}
                </button>
              </Link>
            ))}
          </div>
          <div className="row mb-2">
            {related.map((link) => (
              <Link to={`/parameter/${site.id}/${link.CODE}`} key={link.ID}>
                <button className="btn btn-light btn-sm mr-3">
                  {link.LABEL}
                </button>
              </Link>
            ))}
          </div>
          <ParameterChart
            name={parameter.CODE}
            data={filtered}
            upperAbsolute={parameter.UPPER_ABS_LIMIT}
            upperCheck={parameter.UPPER_CHK_LIMIT}
            lowerCheck={parameter.LOWER_CHK_LIMIT}
            lowerAbsolute={parameter.LOWER_ABS_LIMIT}
          />
        </div>
      </div>
    </div>
  );
};

const getTableRowClass = (parameter, record) => {
  let className = "";
  if (
    parameter.UPPER_ABS_LIMIT &&
    record.PARAM_VALUE >= parameter.UPPER_ABS_LIMIT
  )
    className = "text-danger table-danger";
  if (
    parameter.UPPER_CHK_LIMIT &&
    record.PARAM_VALUE >= parameter.UPPER_CHK_LIMIT
  )
    className = "table-danger";
  if (
    parameter.LOWER_CHK_LIMIT &&
    record.PARAM_VALUE <= parameter.LOWER_CHK_LIMIT
  )
    className = "table-danger";
  if (
    parameter.LOWER_ABS_LIMIT &&
    record.PARAM_VALUE <= parameter.LOWER_ABS_LIMIT
  )
    className = "table-danger";
  return className;
};

export default Parameter;
